import { Component, inject, OnInit } from '@angular/core';
import { OttoOlsenAvailabilityModalComponent } from '../otto-olsen-availability-modal/otto-olsen-availability-modal.component';
import { DatePipe } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { endOfDay, startOfWeek, endOfWeek, addWeeks, addMonths, startOfMonth, endOfMonth, isWithinInterval, getMonth, getYear } from 'date-fns';
import { DateUtilService, GungLanguageConfigService } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs';
import { AuthService } from 'gung-standard';

@Component({
  selector: 'otto-olsen-availability-warehouses-modal',
  templateUrl: './otto-olsen-availability-warehouses-modal.component.html',
  styleUrls: ['./otto-olsen-availability-warehouses-modal.component.scss'],
  standalone: false
})
export class OttoOlsenAvailabilityWarehousesModalComponent extends OttoOlsenAvailabilityModalComponent implements OnInit {
  protected authService = inject(AuthService);

  availabilities: {
    class: string;
    stock: number;
    date: Date;
    info: 'TODAY' | 'CURRENT_WEEK' | 'NEXT_WEEK' | 'AFTER_NEXT_WEEK' | 'NEXT_MONTH' | string;
  }[] = [];

  isAnonymous: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    protected datePipe: DatePipe,
    public dateUtilService: DateUtilService,
    protected translateService: TranslateService,
    protected languageConfigService: GungLanguageConfigService
  ) {
    super(activeModal, dateUtilService, translateService, languageConfigService);
  }

  ngOnInit(): void {
    this.authService.getRoles().pipe(first()).subscribe(roles => {
      this.isAnonymous = roles.includes('ANONYMOUS') && roles.length === 1;

      if (this.availability.extra.availabilities) {
        for (const av in this.availability.extra.availabilities) {
          if (Object.prototype.hasOwnProperty.call(this.availability.extra.availabilities, av)) {
            const stock = this.availability.extra.availabilities[av];
            this.availabilities.push({
              class: stock > 0 ? 'stock' : 'unavailable',
              stock,
              date: this.parseDate(av),
              info: this.getInfoAvailability(this.parseDate(av))
            });
          }
        }
      }
    });
  }

  getInfoAvailability(d: Date): 'TODAY' | 'CURRENT_WEEK' | 'NEXT_WEEK' | 'AFTER_NEXT_WEEK' | 'NEXT_MONTH' | string {
    const today = endOfDay(new Date());

    const weekStart = startOfWeek(today);
    const weekEnd = endOfWeek(today);

    const nextWeek = addWeeks(new Date(), 1);
    const nextWeekStart = startOfWeek(nextWeek);
    const nextWeekEnd = endOfWeek(nextWeek);

    const nextMonth = addMonths(new Date(), 1);
    const nextMonthStart = startOfMonth(nextMonth);
    const nextMonthEnd = endOfMonth(nextMonth);

    if (d < today) {
      return 'TODAY';
    } else if (isWithinInterval(d, { start: weekStart, end: weekEnd })) {
      return 'CURRENT_WEEK';
    } else if (isWithinInterval(d, { start: nextWeekStart, end: nextWeekEnd })) {
      return 'NEXT_WEEK';
    } else if (isWithinInterval(d, { start: nextMonthStart, end: nextMonthEnd })) {
      return 'NEXT_MONTH';
    } else if ((nextWeekEnd < nextMonthStart) && isWithinInterval(d, { start: nextWeekEnd, end: nextMonthStart })) {
      return 'AFTER_NEXT_WEEK';

    }
    const month = getMonth(d);
    const year = getYear(d);
    const currentYear = getYear(new Date());
    if (year <= currentYear) {
      return this.datePipe.transform(d, 'MMMM');
    }
    return this.datePipe.transform(d, 'MMMM YYYY');
  }

  getValueForAnonymous(stockNumber: number): string {
    let currentValue: string = String(stockNumber);
    if (stockNumber >= 1000) {
      currentValue = `1000+`;
    } else if (stockNumber >= 100) {
      currentValue = `100+`;
    } else if (stockNumber >= 50) {
      currentValue = `50+`;
    } else if (stockNumber >= 10) {
      currentValue = `10+`;
    }

    return currentValue;
  }
}
